
import { Component, Prop, Vue } from 'vue-property-decorator';
import { Getter } from 'vuex-class';
import { ValidationObserver, ValidationProvider } from 'vee-validate';
import { Provider, Validate } from '@/types';
import { ROLES, KIOSK_FIELD_ROLES } from '@/utils/constants';
import { PERMISSION } from '@/permissions';
import InfoBanner from '@/components/shared/suites/InfoBanner.vue';
import TextButton from '@/components/shared/suites/TextButton.vue';
import TextField from '@/components/shared/TextField.vue';
import SelectComponent from '@/components/shared/Select.vue';
import '@/validation-rules';

@Component<UserForm>({
	components: {
		InfoBanner,
		TextButton,
		TextField,
		SelectComponent,
		ValidationObserver,
		ValidationProvider
	}
})
export default class UserForm extends Vue {
	@Getter('getRoleLevel', { namespace: 'auth' }) private roleLevel!: number;
	@Getter('hasPermission', { namespace: 'auth' }) private hasPermission!: (permission: PERMISSION) => boolean;
	@Prop({ type: Object, required: true }) private value!: UserFormValue;
	@Prop({ type: String, default: 'create' }) private mode!: 'create' | 'edit';
	@Prop({ type: Boolean, default: false }) private loading!: boolean;

	$refs!: { observer: Validate; passwordProvider: Provider; };

	private PERMISSION = PERMISSION;
	private showPassword: boolean = false;

	private get email(): string {
		return this.value.email;
	}
	private set email(val: string) {
		this.emitUpdate({ email: val });
	}

	private get password(): string {
		return this.value.password;
	}
	private set password(val: string) {
		this.emitUpdate({ password: val });
	}

	private get role(): string {
		return this.value.role;
	}
	private set role(selectedRole: string) {
		// Clear kiosk number and pins when switching to a role that doesn't support them
		if (!KIOSK_FIELD_ROLES.includes(selectedRole)) {
			this.emitUpdate({ role: selectedRole, kioskNumber: '', pins: '' });
		}
		else {
			this.emitUpdate({ role: selectedRole });
		}
	}

	private get kioskNumber(): string {
		return this.value.kioskNumber;
	}
	private set kioskNumber(value: string) {
		this.emitUpdate({ kioskNumber: value });
	}

	private get pins(): string {
		return this.value.pins;
	}
	private set pins(value: string) {
		this.emitUpdate({ pins: value });
	}

	private get roleOptions(): { text: string; value: string; disabled: boolean }[] {
		const isAdmin = this.roleLevel === ROLES.admin;
		return Object.keys(ROLES)
			.map(role => {
				const level = ROLES[role as keyof Roles];
				return {
					text: this.$t(`multi_location_settings.user_management.roles.${role}`),
					value: role,
					// Non-admins may only assign roles less privileged than their own and never the staff/kiosk roles
					disabled: !(isAdmin || (level > this.roleLevel && level < ROLES.staff))
				};
			})
			// Keep enabled roles and the user's own current role in case it's disabled
			.filter(option => !option.disabled || option.value === this.value.role);
	}

	private get canHaveStaffFields(): boolean {
		return KIOSK_FIELD_ROLES.includes(this.value.role);
	}

	/**
	 * Trigger validation across all fields. Parents call this before submitting.
	 *
	 * @return {Promise<boolean>}
	 */
	public async validate(): Promise<boolean> {
		return this.$refs.observer.validate();
	}

	/**
	 * Clear the password field. Called by the parent after a successful password update.
	 *
	 * @return {void}
	 */
	public clearPassword(): void {
		this.emitUpdate({ password: '' });
		this.$refs.passwordProvider.reset();
	}

	/**
	 * Validate the password field and, if valid, emit the new password to the parent.
	 *
	 * @return {Promise<void>}
	 */
	private async updatePassword(): Promise<void> {
		const { valid } = await this.$refs.passwordProvider.validate();
		if (!valid) {
			return;
		}
		this.$emit('update-password', this.password);
	}

	private emitUpdate(partial: Partial<UserFormValue>): void {
		this.$emit('input', { ...this.value, ...partial });
	}
}
